import L from 'leaflet';
import { labelPointOf } from './filteredAreaBadges.js';
import { removeComparedArea } from './compareAreas.js';

// The compared areas' numbers on the map (both views): a dark disc with the area's column number in the comparison
// card (CompareCard.js), at the point deepest inside the area (labelPointOf), on a pane of its own above the homes
// and below popups. Only the disc takes the mouse. A click on it takes the area out of the comparison (the owner,
// 2026-11-01): with a mouse, pointing at it turns its number into an X (compareCard.css) and shows a short hint for
// a moment ("Nr 1 i jämförelsen · Klicka för att ta bort"); on a touch screen a tap takes it out at once, nothing
// animated. The click never reaches the map, and the rest of a double click (or a second tap) that then lands on
// the area under it is swallowed for a moment, or it would pick the area or add it straight back.

const PANE = 'compareMarkerPane';
const PANE_Z = 648;
const FOLLOW_UP_MS = 500;
const X_ICON = '<svg class="compare-marker-x" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';

// getLayer(code): the area's Leaflet layer, or null while it isn't drawn. view: for the analytics; getSwedish():
// the language now (refresh() redraws the hints after a switch).
export const createCompareMarkers = (map, getLayer, { view = 'kartvy', getSwedish = () => true } = {}) => {
    if (!map.getPane(PANE)) {
        const pane = map.createPane(PANE);
        pane.style.zIndex = String(PANE_Z);
        pane.style.pointerEvents = 'none';
    }
    const markers = new Map(); // code -> { marker, number }
    const points = new Map(); // code -> latlng (an area's point doesn't change)

    const hint = (number) => (getSwedish()
        ? `Nr ${number} i jämförelsen · Klicka för att ta bort`
        : `No. ${number} in the comparison · Click to remove`);
    const label = (number) => (getSwedish() ? `Ta bort nr ${number} ur jämförelsen` : `Remove no. ${number} from the comparison`);
    const iconFor = (number) => L.divIcon({
        className: 'compare-map-marker',
        html: `<span class="compare-marker-num" role="button" aria-label="${label(number)}"><b>${number}</b>${X_ICON}</span>`
            + `<span class="compare-marker-hint" role="tooltip">${hint(number)}</span>`,
        iconSize: [24, 24],
        iconAnchor: [12, 12],
    });

    const swallowFollowUp = () => {
        const container = map.getContainer();
        const swallow = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        container.addEventListener('click', swallow, true);
        container.addEventListener('dblclick', swallow, true);
        setTimeout(() => {
            container.removeEventListener('click', swallow, true);
            container.removeEventListener('dblclick', swallow, true);
        }, FOLLOW_UP_MS);
    };

    const update = (areas) => {
        const wanted = new Set();
        (areas || []).forEach((area, index) => {
            const number = index + 1;
            let point = points.get(area.code);
            if (!point) {
                const layer = getLayer(area.code);
                if (!layer) return;
                point = labelPointOf(layer);
                points.set(area.code, point);
            }
            wanted.add(area.code);
            const existing = markers.get(area.code);
            if (existing) {
                if (existing.number !== number) {
                    existing.marker.setIcon(iconFor(number));
                    existing.number = number;
                }
                return;
            }
            const marker = L.marker(point, {
                pane: PANE,
                interactive: false,
                keyboard: false,
                icon: iconFor(number),
            }).addTo(map);
            // The icon's element is kept when its number changes (a divIcon reuses it), so these stay.
            const element = marker.getElement();
            if (element) {
                L.DomEvent.disableClickPropagation(element);
                const code = area.code;
                element.addEventListener('click', (event) => {
                    event.preventDefault();
                    removeComparedArea(code, { view, how: 'map_number' });
                    swallowFollowUp();
                });
            }
            markers.set(area.code, { marker, number });
        });
        markers.forEach((entry, code) => {
            if (wanted.has(code)) return;
            entry.marker.remove();
            markers.delete(code);
        });
    };

    return {
        update,
        refresh: () => markers.forEach((entry) => entry.marker.setIcon(iconFor(entry.number))),
        destroy: () => {
            markers.forEach((entry) => entry.marker.remove());
            markers.clear();
        },
    };
};
